import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Send, Users, CalendarDays, Clock, Copy, ArrowLeft, Instagram, Swords } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import FloatingMenuButton from "@/components/FloatingMenuButton";
import usePageTexts from "@/hooks/usePageTexts";
import { Avatar, Podium } from "@/components/Podium";

function GamesSection({ games, t }) {
  if (!games.live.length && !games.finished.length) return null;
  const names = (team) => team.map((x) => x.name).join(" & ");
  return (
    <section data-testid="dayuse-games" className="mb-10">
      <div className="flex items-center gap-2"><Swords className="h-6 w-6 text-[#E8611A]" /><h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">{t("dayuse_games_title")}</h2></div>
      <div className="mt-4 space-y-3">
        {games.live.map((g) => (
          <div key={g.id} data-testid={`public-live-game-${g.id}`} className="rounded-3xl bg-[#0b1610] p-4 text-white shadow-xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-black uppercase"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> Ao vivo</span>
            <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
              <div className="flex flex-col items-center gap-1 text-center"><div className="flex -space-x-3">{g.team_a.map((p) => <Avatar key={p.user_id} r={p} size="h-12 w-12 ring-2 ring-[#0b1610]" />)}</div><p className="text-xs font-semibold">{names(g.team_a)}</p></div>
              <p data-testid={`public-score-${g.id}`} className="font-display text-4xl font-black tabular-nums">{g.score_a} <span className="text-white/40">x</span> {g.score_b}</p>
              <div className="flex flex-col items-center gap-1 text-center"><div className="flex -space-x-3">{g.team_b.map((p) => <Avatar key={p.user_id} r={p} size="h-12 w-12 ring-2 ring-[#0b1610]" />)}</div><p className="text-xs font-semibold">{names(g.team_b)}</p></div>
            </div>
          </div>
        ))}
        {games.finished.slice(0, 5).map((g) => (
          <div key={g.id} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm">
            <span className={g.winner === "a" ? "font-bold text-[#1B7A45]" : "text-slate-500"}>{names(g.team_a)}</span>
            <span className="font-display font-black text-slate-900">{g.score_a} x {g.score_b}</span>
            <span className={`text-right ${g.winner === "b" ? "font-bold text-[#1B7A45]" : "text-slate-500"}`}>{names(g.team_b)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function DayUsePage() {
  const t = usePageTexts();
  const [games, setGames] = useState({ live: [], finished: [] });
  const [data, setData] = useState({ active: false, event: null, confirmations: [], ranking: [] });
  const [form, setForm] = useState({ name: "", instagram: "" });
  const [submitting, setSubmitting] = useState(false);
  const [logo, setLogo] = useState(null);

  const load = () =>
    api.get("/dayuse").then((res) => setData(res.data)).catch(() => {});

  useEffect(() => {
    load();
    api.get("/settings").then((res) => {
      if (res.data.dayuse_logo) setLogo(`${API}/settings/dayuse-logo?v=${encodeURIComponent(res.data.dayuse_logo_updated_at)}`);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const poll = () => {
      api.get("/dayuse/games").then((r) => setGames(r.data)).catch(() => {});
      api.get("/dayuse").then((res) => setData(res.data)).catch(() => {});
    };
    api.get("/dayuse/games").then((r) => setGames(r.data)).catch(() => {});
    const id = setInterval(poll, 8000);
    return () => clearInterval(id);
  }, []);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Informe seu nome.");
    setSubmitting(true);
    try {
      const res = await api.post("/dayuse/confirm", {
        name: form.name.trim(),
        instagram: form.instagram.trim(),
      });
      setData((d) => ({ ...d, confirmations: [...d.confirmations, res.data] }));
      setForm({ name: "", instagram: "" });
      toast.success("Presença confirmada! Você está na lista.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const copyPix = (key) => {
    navigator.clipboard?.writeText(key);
    toast.success("Chave PIX copiada!");
  };

  const { active, event, confirmations, ranking } = data;

  return (
    <div className="min-h-screen bg-[#FBFBFB]" data-testid="dayuse-page">
      {/* Hero */}
      <header className="relative overflow-hidden bg-[#0b1610]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0b1610] via-[#123021] to-[#0b1610]" />
        <div className="relative mx-auto max-w-4xl px-5 py-12 text-center">
          <Link
            to="/"
            data-testid="dayuse-back-home"
            className="absolute left-5 top-5 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-white/90 backdrop-blur transition hover:bg-white/20"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Início
          </Link>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-emerald-300">
            {t("dayuse_kicker")}
          </p>
          {logo && <img src={logo} alt="" data-testid="dayuse-logo" className="mx-auto mb-2 mt-3 w-72 object-contain" />}
          <h1 data-testid="dayuse-title" className="animate-fade-up font-display text-4xl font-black tracking-tight text-white sm:text-5xl lg:text-6xl">
            {t("dayuse_title")}<span className="text-[#E8611A]">{t("dayuse_title_accent")}</span>
          </h1>
          <p className="animate-fade-up mt-2 text-base font-medium text-emerald-100/80" style={{ animationDelay: "0.1s" }}>
            {t("dayuse_subtitle")}
          </p>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-5 pb-28 pt-10">
        <GamesSection games={games} t={t} />

        {/* Ranking */}
        {ranking.length > 0 && (
          <section data-testid="dayuse-ranking" className="mb-10">
            <div className="flex items-center gap-2">
              <Crown className="h-6 w-6 text-[#E8611A]" />
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
                {t("dayuse_ranking_title")}
              </h2>
            </div>
            <p className="mt-1 text-sm text-slate-500">{t("dayuse_ranking_subtitle")}</p>
            <Podium ranking={ranking} />
            <div className="mt-4 space-y-2">
              {ranking.slice(3).map((r, i) => (
                <div
                  key={r.id}
                  data-testid={`dayuse-rank-${r.id}`}
                  className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-2.5 shadow-sm"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="w-8 text-center font-display text-sm font-black text-slate-400">{i + 4}º</span>
                    <Avatar r={r} size="h-10 w-10" />
                    <div className="min-w-0">
                      <p className="truncate font-display text-base font-bold text-slate-900">{r.name}</p>
                      {r.instagram && (
                        <a
                          href={`https://www.instagram.com/${r.instagram.replace(/^@/, "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs font-medium text-[#1B7A45] hover:underline"
                        >
                          <Instagram className="h-3 w-3" /> {r.instagram}
                        </a>
                      )}
                    </div>
                  </div>
                  <span className="font-display text-xl font-black text-[#1B7A45]">
                    {r.points} <span className="text-xs font-semibold text-slate-400">pts</span>
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {active && event ? (
          <>
            {/* Event info */}
            <section
              data-testid="dayuse-event-info"
              className="mb-6 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#EAF5EE]">
                    <CalendarDays className="h-5 w-5 text-[#1B7A45]" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Data</p>
                    <p className="font-semibold text-slate-900">{event.date || "A definir"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#EAF5EE]">
                    <Clock className="h-5 w-5 text-[#1B7A45]" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Horário</p>
                    <p className="font-semibold text-slate-900">{event.time || "A definir"}</p>
                  </div>
                </div>
                {event.pix_key && (
                  <div className="flex items-center gap-3">
                    <button
                      data-testid="dayuse-pix-copy"
                      onClick={() => copyPix(event.pix_key)}
                      className="flex items-center gap-2 rounded-xl bg-[#122018] px-3 py-2 text-left text-white transition hover:bg-[#1a2e21]"
                    >
                      <Copy className="h-4 w-4 shrink-0 text-emerald-300" />
                      <span className="min-w-0">
                        <span className="block text-[10px] font-semibold uppercase tracking-wider text-emerald-300">
                          PIX{event.pix_holder ? ` · ${event.pix_holder}` : ""}
                        </span>
                        <span className="block truncate text-sm font-bold">{event.pix_key}</span>
                      </span>
                    </button>
                  </div>
                )}
              </div>
              {event.info && (
                <p className="mt-4 rounded-xl bg-amber-50 px-4 py-2.5 text-sm text-amber-800">{event.info}</p>
              )}
            </section>

            {/* Sign-up form */}
            <section className="mb-6 rounded-3xl border border-[#1B7A45]/30 bg-[#EAF5EE] p-5">
              <h3 className="font-display text-lg font-bold text-[#0E4325]">Confirmar minha presença</h3>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto]">
                <Input
                  data-testid="dayuse-form-name"
                  placeholder="Seu nome"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="rounded-xl border-slate-200 bg-white"
                />
                <Input
                  data-testid="dayuse-form-instagram"
                  placeholder="@seuinstagram"
                  value={form.instagram}
                  onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                  className="rounded-xl border-slate-200 bg-white"
                />
                <Button
                  data-testid="dayuse-submit"
                  onClick={submit}
                  disabled={submitting}
                  className="rounded-xl bg-[#1B7A45] px-6 py-5 font-semibold text-white hover:bg-[#156337] disabled:opacity-50"
                >
                  <Send className="mr-2 h-4 w-4" />
                  {submitting ? "Enviando..." : "Confirmar"}
                </Button>
              </div>
            </section>

            {/* Confirmed list */}
            <section data-testid="dayuse-confirmed-list">
              <div className="flex items-center gap-2">
                <Users className="h-6 w-6 text-[#1B7A45]" />
                <h2 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
                  {t("dayuse_confirmed_title")}
                </h2>
              </div>
              <p className="mt-1 text-sm text-slate-500">{confirmations.length} confirmado(s)</p>
              <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {confirmations.length === 0 ? (
                  <div className="col-span-full rounded-2xl border border-dashed border-slate-300 bg-white py-10 text-center text-sm text-slate-400">
                    Seja o primeiro a confirmar!
                  </div>
                ) : (
                  confirmations.map((c, i) => (
                    <div
                      key={c.id}
                      data-testid={`dayuse-confirmed-${c.id}`}
                      className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#EAF5EE] text-xs font-bold text-[#1B7A45]">
                        {i + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-slate-900">{c.name}</p>
                        {c.instagram && (
                          <a
                            href={`https://www.instagram.com/${c.instagram.replace(/^@/, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-medium text-[#E8611A] hover:underline"
                          >
                            <Instagram className="h-3 w-3" /> {c.instagram}
                          </a>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>
          </>
        ) : (
          <div
            data-testid="dayuse-inactive-msg"
            className="rounded-3xl border border-dashed border-slate-300 bg-white py-16 text-center"
          >
            <Users className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 font-display text-xl font-bold text-slate-700">
              {t("dayuse_inactive_title")}
            </p>
            <p className="mt-1 text-sm text-slate-400">
              {t("dayuse_inactive_subtitle")}
            </p>
          </div>
        )}
      </main>

      <FloatingMenuButton />
    </div>
  );
}
